import { EditorState, Modifier, SelectionState } from 'draft-js';
import { createRef, PureComponent } from 'react';

import { getSelectedEntity } from '../../utils';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const LINK = 'LINK';

export default class LinkEditor extends PureComponent {
  state = { url: '', text: '', internalLink: false };
  textInputEl = createRef();

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      const { editorState, onUpdateEditorState } = this.props;
      let selection = editorState.getSelection();
      const origSelection = selection;
      const startKey = selection.getStartKey();
      const contentState = editorState.getCurrentContent();
      const block = contentState.getBlockForKey(startKey);
      const blockText = block.getText();

      // If multiple blocks are selected, change selection to only the initial block
      if (startKey !== selection.getEndKey()) {
        selection = new SelectionState({
          anchorKey: selection.getStartKey(),
          anchorOffset: selection.getStartOffset(),
          focusKey: selection.getStartKey(),
          focusOffset: blockText.length,
        });
      }

      const existingEntity = this.getExistingEntity();
      if (existingEntity) {
        const { entity, key: entityKey } = existingEntity;
        const { internalLink, url } = entity.getData();
        this.setState({ url, internalLink });
        const list = block.getCharacterList();
        selection = selection.merge({
          anchorOffset: list.findIndex(c => c.getEntity() === entityKey),
          focusOffset: list.findLastIndex(c => c.getEntity() === entityKey) + 1,
        });
      }
      if (selection !== origSelection) {
        // timeout because editOnBlue handler sometimes fires right after and reverts selection
        setTimeout(() => onUpdateEditorState(EditorState.acceptSelection(editorState, selection)), 0);
      }
      const text = blockText.substring(selection.getStartOffset(), selection.getEndOffset());
      this.setState({ text }, () => this.textInputEl.current?.focus());
    }
  }

  getExistingEntity() {
    const { editorState } = this.props;
    const selectedEntity = getSelectedEntity(editorState);
    if (selectedEntity?.entity?.type === LINK) {
      return selectedEntity;
    }
  }

  confirmLink = () => {
    const { editorState, onClose, onUpdateEditorState } = this.props;
    const { internalLink, text, url } = this.state;
    let contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    let entityKey;

    const existingEntity = this.getExistingEntity();
    if (existingEntity) {
      const { key: existingKey } = existingEntity;
      contentState = contentState.mergeEntityData(existingKey, { url, internalLink });
      entityKey = existingKey;
    } else {
      contentState = contentState.createEntity(LINK, 'MUTABLE', { url, internalLink });
      entityKey = contentState.getLastCreatedEntityKey();
    }

    contentState = Modifier.replaceText(contentState, selection, text, null, entityKey);

    const newSelOffset = selection.getStartOffset() + (text || '').length;

    const editorStateWithLinkText = EditorState.set(editorState, {
      currentContent: contentState,
      selection: selection.merge({ focusOffset: newSelOffset, anchorOffset: newSelOffset, hasFocus: true }),
      forceSelection: true,
    });

    onUpdateEditorState(editorStateWithLinkText);
    onClose();
    this.setState({ url: '' });
  };

  onInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.confirmLink();
    }
  };

  toggleInternalLink = () => this.setState(({ internalLink }) => ({ internalLink: !internalLink }));

  render() {
    const { anchorEl, disableInternal, onClose, onExited, show } = this.props;
    const { internalLink, text, url } = this.state;

    const isInvalid = !text || !url.match(internalLink ? /^\/[^\s]*$/ : /^((https?:\/\/)|(mailto:))[^\s]+$/);

    return (
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        css={rootCss}
        disableRestoreFocus
        onClose={onClose}
        open={show}
        slotProps={{ paper: { className: 'LE-popover' }, transition: { onExited } }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Typography align="center" variant="h6">
          Insert Link
        </Typography>
        {!disableInternal && (
          <FormControlLabel
            control={<Checkbox checked={internalLink} color="primary" onChange={this.toggleInternalLink} />}
            label="Internal Link"
          />
        )}
        <TextField
          className="LE-textField"
          fullWidth
          inputRef={this.textInputEl}
          label="Display Text"
          onChange={({ target: { value: text } }) => this.setState({ text })}
          onKeyDown={isInvalid ? undefined : this.onInputKeyDown}
          size="small"
          value={text}
        />
        <TextField
          className="LE-textField"
          fullWidth
          label="Website Address"
          margin="normal"
          onChange={({ target: { value } }) => this.setState({ url: value.trim() })}
          onKeyDown={isInvalid ? undefined : this.onInputKeyDown}
          placeholder={internalLink ? ' (should start with "/")' : 'start with "http(s)://" or "mailto:"'}
          size="small"
          slotProps={{
            input:
              internalLink ?
                {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      wizeprep.com
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
          value={url}
        />
        <Button disabled={isInvalid} fullWidth onClick={this.confirmLink} variant="contained">
          Done
        </Button>
      </Popover>
    );
  }
}

const rootCss = ({ spacing, zIndex }) => ({
  zIndex: `${zIndex.modal + 2} !important`,
  '.LE-popover': {
    minWidth: 300,
    padding: spacing(2),
    textAlign: 'center',
  },
  '.LE-textField': { display: 'block' },
});
